import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, Subject, switchMap, take } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { CurrentUserSelectors } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { JobsActions } from '@core/offers-data';
import type { Requisition } from '@core/requisitions-types';
import { DuplicateViewContext, RequisitionsActions } from '@recruitee/requisitions-data';

import { DuplicateDispatcher } from './duplicate-dispatcher';
import { RequisitionsDialogsService } from './requisitions-dialogs.service';
import { RequisitionsAnalytics } from '../analytics/requisitions.analytics';

@Injectable()
export class RequisitionsSharedActionsService {
  private readonly requisitionsDialogs = inject(RequisitionsDialogsService);
  private readonly requisitionsAnalytics = inject(RequisitionsAnalytics);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);

  private duplicateDispatcher = new DuplicateDispatcher();
  private currentAdmin: CurrentUser;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()), filterDefined)
      .subscribe(currentAdmin => (this.currentAdmin = currentAdmin));
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public duplicate(requisitionId: number): void {
    this.requisitionsDialogs
      .confirmDuplicate$()
      .pipe(
        take(1),
        filterBoolean,
        switchMap(() =>
          this.duplicateDispatcher.execute$(requisitionId, DuplicateViewContext.Page),
        ),
        take(1),
      )
      .subscribe();
  }

  public archive(requisition: Requisition, redirectToRequisitionPage?: boolean): void {
    this.requisitionsDialogs
      .confirmArchive$(requisition.status)
      .pipe(
        take(1),
        filterBoolean,
        tap(() => {
          this.store.dispatch(
            RequisitionsActions.archive({
              payload: { id: requisition.id },
            }),
          );
        }),
      )
      .subscribe();
  }

  public retrieve(requisition: Requisition, redirectToRequisitionPage?: boolean): void {
    this.requisitionsDialogs
      .confirmRetrieve$(requisition.status)
      .pipe(
        take(1),
        filterBoolean,
        tap(() => {
          this.store.dispatch(
            RequisitionsActions.retrieve({
              payload: { id: requisition.id },
            }),
          );
        }),
      )
      .subscribe();
  }

  public delete(requisition: Requisition, redirectToList?: boolean): void {
    this.requisitionsDialogs
      .confirmDeleteRequisition$(requisition.title)
      .pipe(
        take(1),
        filterBoolean,
        tap(() => {
          this.store.dispatch(
            RequisitionsActions.removeOnePessimistic({
              payload: { data: requisition.id },
            }),
          );
        }),
        switchMap(() =>
          this.actions$.pipe(ofType(RequisitionsActions.removeOnePessimisticSuccess), take(1)),
        ),
      )
      .subscribe(() => {
        if (redirectToList) this.router.navigate(['/requisitions']);
      });
  }

  public createJob(requisitionId: number, skipConfirmDialog: boolean = false): void {
    const isDialogDisabledByUser =
      this.currentAdmin.featureDiscovery.createJobFromRequisitionSkipConfirm;

    const confirmation$ =
      skipConfirmDialog || !!isDialogDisabledByUser
        ? of(true)
        : this.requisitionsDialogs.confirmCreateJob$().afterClosed.pipe(
            take(1),
            filter(result => result?.confirmed),
            tap(result => {
              if (result.payload.dontShowAgain) {
                this.store.dispatch(
                  FeatureDiscoveryActions.update({
                    payload: { createJobFromRequisitionSkipConfirm: true },
                  }),
                );
              }
            }),
            map(() => true),
          );

    confirmation$
      .pipe(
        take(1),
        filterBoolean,
        tap(() =>
          this.store.dispatch(JobsActions.createFromRequisition({ payload: { requisitionId } })),
        ),
        switchMap(() =>
          this.actions$.pipe(ofType(JobsActions.createFromRequisitionSuccess), take(1)).pipe(
            tap(({ payload: { data: offer } }) => {
              this.router.navigate(['offers', offer.slug, 'edit'], { replaceUrl: true });
              this.requisitionsAnalytics.trackCreateJobFromRequisition();
            }),
          ),
        ),
      )
      .subscribe();
  }

  public cancelApprovalRequest(id: number): void {
    this.requisitionsDialogs
      .confirmCancelRequest$()
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.store.dispatch(
          RequisitionsActions.cancelApprovalRequest({
            payload: { id },
          }),
        );
      });
  }

  public follow(id: number): void {
    this.store.dispatch(RequisitionsActions.follow({ payload: { id } }));
  }

  public unfollow(id: number): void {
    this.store.dispatch(RequisitionsActions.unfollow({ payload: { id } }));
  }
}
